/**
 * AI 생성 화면 미리보기 — 실제 공통 컴포넌트(SearchPanel + SnAgGrid)로 그대로 렌더한다.
 * 별도 디자인 요소(툴바/배지/속성 패널) 없이, 배포된 실제 ERP 화면과 100% 동일한 모습.
 * - 임시저장/배포는 SNotes AI Bot 채팅("임시저장해줘"/"배포해줘")으로 실행
 * - 조회는 상단 공통 툴바(돋보기) → 탭 ref → onSearch로 실행.
 *   쿼리 ID는 {화면코드소문자}_01 관례를 쓴다. snotes-ai는 임시저장·배포 시점에
 *   sys_query(cd_query = {이슈번호}_01/_02 …)를 채우므로, 저장 전에는 조회할 쿼리가
 *   아직 없다 — 이때는 조회를 시도하지 않고 안내만 남긴다.
 *   (새 계약에는 소스 화면 queryId가 없다. 프론트에서 쿼리 ID를 지어내지 않는다)
 * - 2그리드(헤더/내역): meta.grids를 세로로 쌓아 렌더.
 *   조회 결과셋을 그리드 순서대로 바인딩한다(data[0]→grid1, data[1]→grid2 …).
 *   조회 SQL이 SELECT 여러 문장이면 서버가 결과셋을 그만큼 주므로 운영 화면과 같이
 *   조회 한 번에 헤더/내역이 함께 찬다. 남는 결과셋(sheet1 등)은 그리지 않는다.
 *   grid1 행 클릭 → meta.queries.grid2(내역 SQL 초안)가 있으면 실행해 grid2를 다시 바인딩
 */
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchPanel from "@widgets/SearchPanel";
import SnAgGrid from "@shared/components/Grid/SnGrid";
import { fnGetDataSet } from "@shared/api/restApi";
import { getTablebySql } from "@shared/utils/CommonUtils";
import { global } from "@shared/libs/Global";
import { ColumnConfig } from "@shared/components/Grid/types";
import { menuInfo } from "@shared/type/CommonType";
import type { StudioModel } from "../studioTypes";
import type { PreviewMeta, PreviewGrid } from "../previewMeta";
import { describeError } from "../errorText";

/** 화면 코드가 아직 없을 때 SnAgGrid 컬럼설정이 실제 메뉴에 저장되지 않도록 쓰는 임시 키 */
const PREVIEW_CD_MENU = "AI_SCREEN_PREVIEW";

interface Props {
  screen: StudioModel;
  /** 공통 컴포넌트에 넣을 메타 (sys_formctrl/sys_formgridcolumn snake_case 행) */
  meta: PreviewMeta;
  /** 현재 탭(메뉴디자이너)의 Menuprops — cd_module 참조용 */
  Menuprops: menuInfo;
}

const EditorView = forwardRef(({ screen, meta, Menuprops }: Props, ref) => {
  const searchRef = useRef<any>(null);
  // 그리드별 데이터 — [0]=grid1(헤더), [1]=grid2(내역)
  const [dataSources, setDataSources] = useState<any[][]>([[]]);
  const [notice, setNotice] = useState<string | null>(null);
  // 그리드 컬럼은 편집(헤더 X로 제거) 가능하도록 로컬 상태로 관리
  const [grids, setGrids] = useState<PreviewGrid[]>(meta.grids);

  // 새 화면이 적용되면 그리드 데이터/컬럼/안내 초기화
  useEffect(() => {
    setDataSources([[]]);
    setGrids(meta.grids);
    setNotice(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta]);

  // SearchPanel/SnAgGrid에 넘길 메뉴 정보: 생성 중인 화면 기준
  const previewMenuInfo: menuInfo = {
    cd_menu: screen.code || PREVIEW_CD_MENU,
    nm_menu: screen.title,
    cd_module: Menuprops?.cd_module,
  };

  const setGridData = (index: number, rows: any[]) => {
    setDataSources((prev) => {
      const next = [...prev];
      next[index] = rows;
      return next;
    });
  };

  // 상단 공통 툴바(돋보기) → 탭 ref → 여기로 전달되는 조회 (전체 그리드 바인딩).
  // 조회쿼리는 임시저장·배포 시점에 서버가 {이슈번호}_01로 채우므로 그 관례를 따른다.
  const onSearch = async () => {
    const id = screen.code ? `${screen.code.toLowerCase()}_01` : "";
    if (!id) {
      setNotice(
        "아직 화면 코드가 정해지지 않아 조회할 수 없습니다. 임시저장 또는 배포 후 조회해 주세요."
      );
      return;
    }
    try {
      const searchData = searchRef.current?.getFormdata?.() ?? {};
      const data: any = await fnGetDataSet({ id, params: searchData });
      // fnGetDataSet은 백엔드 오류 응답(if_status='E')이면 배열이 아닌 값을 돌려준다
      if (!data || data.status === "cancelled" || !Array.isArray(data)) {
        setDataSources([[]]);
        setNotice(
          `조회 쿼리가 아직 준비되지 않았습니다. 임시저장 또는 배포 후 조회해 주세요. (sys_query: ${id})`
        );
        return;
      }
      setNotice(null);
      // 결과셋을 그리드 순서대로 바인딩한다 (data[0]→grid1, data[1]→grid2 …).
      // 조회 SQL이 SELECT 여러 문장이면 서버가 그만큼 결과셋을 돌려주므로,
      // 운영 화면(SN_SAL_SAO_REG_001)처럼 조회 한 번에 헤더/내역이 함께 채워진다.
      // 결과셋 개수와 그리드 개수는 다를 수 있다 —
      //  · 남는 결과셋(sheet1 등 화면에 안 그리는 것)은 버리고
      //  · 모자라면 빈 배열로 둔다(내역은 행 클릭 시 grid2 쿼리로 채워질 수 있다)
      setDataSources(
        grids.map((_g, i) => (Array.isArray(data[i]) ? data[i] : []))
      );
    } catch (error) {
      // 여기까지 온 건 통신/세션/서버 예외다. "쿼리 미준비"로 뭉개면 원인을 못 찾는다.
      setDataSources([[]]);
      setNotice(`조회 중 오류가 발생했습니다. ${describeError(error)} (sys_query: ${id})`);
    }
  };

  // grid1 행 클릭 → grid2 조회쿼리 초안(draftQueries.grid2)이 있으면 실행해 grid2 바인딩
  // named param(:no_req 등)은 사용자 컨텍스트 + 클릭한 행 값으로 채워진다
  const onGrid1RowClicked = async (event: any) => {
    const row = event?.data;
    // 그리드가 하나뿐이면 내역이라는 개념 자체가 없다 (정상 — 알릴 것도 없다)
    if (!row || grids.length < 2) return;
    const sql = meta.queries?.grid2;
    if (!sql) {
      // 예전엔 조용히 return이라 "클릭해도 아무 일이 없는" 이유를 알 수 없었다.
      setNotice(
        "내역(grid2) 조회 SQL이 없어 행을 클릭해도 내역이 바뀌지 않습니다. " +
          "지금 보이는 내역은 조회 결과셋으로 채워진 것입니다. " +
          "행 클릭 조회가 필요하면 챗봇에 내역 조회 쿼리를 요청해 주세요."
      );
      return;
    }
    try {
      const u: any = global.getState().userInfo ?? {};
      // 클릭한 행 값을 먼저 깔고 로그인 컨텍스트를 나중에 덮는다.
      // 반대로 하면 행에 cd_emp/cd_dept 같은 컬럼이 있을 때(ERP 그리드에는 흔하다)
      // grid2 쿼리의 :cd_emp가 로그인 사용자가 아니라 그 행의 담당자로 조용히 바뀐다.
      const rows = await getTablebySql({
        ...row,
        sql,
        cd_firm: u.cd_firm,
        cd_biz: u.cd_biz,
        cd_dept: u.cd_dept,
        cd_emp: u.cd_emp,
        cd_user: u.cd_user,
        cd_exchange: u.cd_exchange,
        fg_pos: u.fg_pos,
        fg_role: u.fg_role,
        cd_gra: u.cd_gra,
        cd_gp: u.cd_gp,
        fg_user: u.fg_user,
        cd_cust: u.cd_cust,
      });
      setGridData(1, Array.isArray(rows) ? rows : []);
      setNotice(null);
    } catch (error) {
      setGridData(1, []);
      setNotice(`내역(grid2) 조회에 실패했습니다. ${describeError(error)}`);
    }
  };

  useImperativeHandle(ref, () => ({
    onSearch,
  }));

  return (
    <div className="h-full flex flex-col" style={{ minHeight: 0, background: "#fff" }}>
      {notice && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "7px 12px",
            fontSize: 12,
            fontWeight: 600,
            color: "#b26a00",
            background: "rgba(245,179,1,0.12)",
            border: "1px solid rgba(245,179,1,0.35)",
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 15 }} /> {notice}
        </div>
      )}
      <div className="mb-1">
        {/* designMode: 드래그&드랍 이동 + hover X 삭제 + 리사이즈 (우클릭 → 추가/편집종료) */}
        <SearchPanel
          ref={searchRef}
          data={meta.formData}
          controls={meta.controls}
          designMode
        ></SearchPanel>
      </div>
      <div className="flex-1 flex flex-col" style={{ minHeight: 0, gap: 4 }}>
        {grids.map((g, i) => (
          <div
            key={g.cdGrid}
            className="flex-1 flex flex-col"
            style={{ minHeight: 0 }}
          >
            {/* 그리드 상단 버튼(sys_headerctrl)은 snotes-ai 계약에 없다 —
                서버가 주지 않는 버튼을 프론트에서 지어내지 않는다 */}
            <div className="flex-1" style={{ minHeight: 0 }}>
              <SnAgGrid
                menuInfo={previewMenuInfo}
                enableAddRow={true}
                columnConfig={g.columns as ColumnConfig[]}
                rowData={dataSources[i] ?? []}
                enableExternalChart={false}
                isEditable={false}
                rowSelection="multiple"
                onRowClicked={
                  i === 0 && grids.length > 1 ? onGrid1RowClicked : undefined
                }
                onDesignColumnRemove={(cdColumn) =>
                  setGrids((prev) =>
                    prev.map((pg, pi) =>
                      pi === i
                        ? {
                            ...pg,
                            columns: pg.columns.filter(
                              (c) => c.cd_column !== cdColumn
                            ),
                          }
                        : pg
                    )
                  )
                }
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
});

export default EditorView;
